import { useCallback, useEffect, useState } from "react";

// Sidebar width logic: phones (<= 768px) use a drawer; tablets (769-1099px) start collapsed to icons; desktops start
// expanded. A choice made with the collapse button is remembered in this browser and wins over the automatic rule.
const KEY = "nav_collapsed";
const DRAWER_MAX = 768;
const TABLET_MAX = 1100;

const readMode = () => {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === "collapsed" || v === "expanded" ? v : "auto";
  } catch {
    return "auto";
  }
};

export default function useSidebarState() {
  const [mode, setMode] = useState(readMode);
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 1280 : window.innerWidth));

  useEffect(() => {
    let frame = null;
    const onResize = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setWidth(window.innerWidth);
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const isDrawer = width <= DRAWER_MAX;
  const autoCollapsed = width > DRAWER_MAX && width < TABLET_MAX;
  const collapsed = !isDrawer && (mode === "collapsed" || (mode === "auto" && autoCollapsed));

  const toggle = useCallback(() => {
    const next = collapsed ? "expanded" : "collapsed";
    setMode(next);
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      /* private window: the choice lasts until the page is closed */
    }
  }, [collapsed]);

  return { collapsed, isDrawer, toggle };
}
